"use client";

import { useRouter } from "next/navigation";
import { api } from "~/trpc/react";
import { 
  StatCard, 
  ActionButton, 
  SectionCard, 
  Callout,
  Button 
} from "~/components/ui";
import {
  ZapIcon,
  CompassIcon,
  BuildingIcon,
  UsersIcon,
  MessageCircleIcon,
  LogInIcon,
  SearchIcon,
  CheckCircleIcon,
  LinkExternalIcon,
} from "~/components/icons";

export function AdminDashboardContent() {
  const router = useRouter();
  const { data: statistics, isLoading, error } = api.statistics.getPublicStatistics.useQuery();

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 px-4 sm:px-6 lg:px-8 py-16">
        <p className="text-lg text-red-600">Failed to load statistics</p>
        <p className="text-sm text-[#60646c]">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-6 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
      {/* Stats Row - Stack on mobile, row on tablet+ */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        <StatCard
          label="Actions"
          value={isLoading ? "..." : formatNumber(statistics?.totalRecords ?? 0)}
          icon={<ZapIcon className="h-5 w-5 text-[#003261]" />}
        />
        <StatCard
          label="Jurisdictions"
          value={isLoading ? "..." : formatNumber(statistics?.totalJurisdictions ?? 0)}
          icon={<CompassIcon className="h-5 w-5 text-[#003261]" />}
        />
        <StatCard
          label="Organizations"
          value={isLoading ? "..." : formatNumber(statistics?.totalOrganizations ?? 0)}
          icon={<BuildingIcon className="h-5 w-5 text-[#003261]" />}
        />
      </div>

      {/* Main Content - Stack on mobile/tablet, side-by-side on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <SectionCard 
          title="Quick Actions" 
          className="w-full"
        >
          <div className="flex flex-col">
            <ActionButton
              title="Manage Accounts"
              description="Create and edit user accounts"
              icon={<UsersIcon className="h-4 w-4 text-[#003261]" />}
              onClick={() => router.push('/accounts')}
            />
            <ActionButton
              title="Manage Actions"
              description="Configure system actions"
              icon={<ZapIcon className="h-4 w-4 text-[#003261]" />}
              onClick={() => router.push('/actions')}
            />
            <ActionButton
              title="Manage Contacts"
              description="Update contact information"
              icon={<MessageCircleIcon className="h-4 w-4 text-[#003261]" />}
              onClick={() => router.push('/contacts')}
            />
            <ActionButton
              title="Manage Jurisdictions"
              description="Set up jurisdictions"
              icon={<CompassIcon className="h-4 w-4 text-[#003261]" />}
              onClick={() => router.push('/jurisdictions')}
            />
            <ActionButton
              title="Manage Organizations"
              description="Organization settings"
              icon={<BuildingIcon className="h-4 w-4 text-[#003261]" />}
              onClick={() => router.push('/organizations')}
            />
          </div>
        </SectionCard>

        <div className="flex flex-col gap-4 sm:gap-6">
          <SectionCard 
            title="Reports"
            className="w-full"
          >
            <div className="flex flex-col">
              <ActionButton
                title="Login Reports"
                description="View user login activity"
                icon={<LogInIcon className="h-4 w-4 text-[#003261]" />}
                onClick={() => router.push('/reports/logins')}
              />
              <ActionButton
                title="Search Reports"
                description="Track search queries"
                icon={<SearchIcon className="h-4 w-4 text-[#003261]" />}
                onClick={() => router.push('/reports/searches')}
              />
            </div>
          </SectionCard>

          <SectionCard 
            title="Invoices"
            className="w-full"
          >
            <div className="flex flex-col gap-5">
              <Callout icon={<CheckCircleIcon className="h-4 w-4 text-[#4c6f90]" />}>
                Invoices are now run automatically and managed through the global financial tools on arello.org
              </Callout>
              <Button
                variant="outline"
                className="w-full gap-3 border-[rgba(0,89,175,0.36)] bg-[rgba(0,85,199,0.04)] text-[rgba(0,50,97,0.7)] hover:bg-[rgba(0,85,199,0.08)]"
                onClick={() => window.open('https://www.arello.org/account/login.cfm?dest=%2Fmanager%2Ffinancial%2Findex%2Ecfm%3F', '_blank')}
              >
                <span className="text-sm sm:text-base">Go to Financial Dashboard</span>
                <LinkExternalIcon className="h-[18px] w-[18px] text-[#003261]" />
              </Button>
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
